import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { countries, type Country } from "@/lib/countries";

interface CountrySelectProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  id?: string;
}

const CountrySelect = React.forwardRef<HTMLButtonElement, CountrySelectProps>(
  ({ value, onValueChange, disabled, error, id }, ref) => {
    const [open, setOpen] = React.useState(false);

    const selectedCountry = React.useMemo(
      () => countries.find((c) => c.code === value),
      [value]
    );

    return (
      <div className="w-full">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              id={id}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              aria-label="Select country"
              disabled={disabled}
              className={cn(
                "w-full justify-between font-normal h-10",
                !value && "text-muted-foreground",
                error && "border-destructive focus-visible:ring-destructive"
              )}
            >
              {selectedCountry ? (
                <span className="flex items-center gap-2 truncate">
                  <span className="text-base">{selectedCountry.flag}</span>
                  <span className="truncate">{selectedCountry.name}</span>
                </span>
              ) : (
                <span>Select country</span>
              )}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[var(--radix-popover-trigger-width)] p-0"
            align="start"
          >
            <Command>
              <CommandInput placeholder="Search country..." />
              <CommandList>
                <CommandEmpty>No country found.</CommandEmpty>
                <CommandGroup>
                  {countries.map((country) => (
                    <CommandItem
                      key={country.code}
                      value={`${country.name} ${country.code}`}
                      onSelect={() => {
                        onValueChange(country.code);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === country.code ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="mr-2 text-base">{country.flag}</span>
                      <span className="truncate">{country.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {error && (
          <p className="text-sm text-destructive mt-1" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

CountrySelect.displayName = "CountrySelect";

export { CountrySelect };
